import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { gameQueries } from '@/lib/queries'
import { ClipboardCopy, Loader2, RefreshCw, Users } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

function formatCredits(microdollars: number): string {
  return `$${(microdollars / 1_000_000).toFixed(2)}`
}

type DashboardData = Awaited<ReturnType<typeof gameQueries.getPaidUsersDashboard>>

export function UserDashboardSection() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await gameQueries.getPaidUsersDashboard())
    }
    catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load paid users')
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const summary = data?.summary
  const users = data?.users ?? []

  const copyEmails = async () => {
    if (users.length === 0)
      return
    try {
      await navigator.clipboard.writeText(users.map(u => u.email).join(', '))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
    catch {
      setError('Could not copy to clipboard')
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-5" />
              User Dashboard — Paid Users
            </CardTitle>
            <CardDescription>
              Everyone who has paid real money (Stripe or Steam purchases). Amounts are lifetime totals in USD.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => copyEmails()} disabled={loading || users.length === 0}>
              <ClipboardCopy className="mr-2 size-4" />
              <span className="ml-2">{copied ? 'Copied!' : `Copy ${users.length} emails`}</span>
            </Button>
            <Button type="button" variant="secondary" onClick={() => load()} disabled={loading}>
              {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : <RefreshCw className="mr-2 size-4" />}
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        {summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Paid users</div>
              <div className="text-lg font-semibold tabular-nums">{summary.paidUserCount}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Total purchased</div>
              <div className="text-lg font-semibold tabular-nums">{formatCredits(summary.totalPurchasedCredits)}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Total top-up (comp)</div>
              <div className="text-lg font-semibold tabular-nums">{formatCredits(summary.totalTopupCredits)}</div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Total balance</div>
              <div className="text-lg font-semibold tabular-nums">{formatCredits(summary.totalBalanceCredits)}</div>
            </div>
          </div>
        )}

        {users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Lang</th>
                  <th className="py-2 pr-4 text-right font-medium">Purchased</th>
                  <th className="py-2 pr-4 text-right font-medium">Top-up</th>
                  <th className="py-2 pr-4 text-right font-medium">Balance</th>
                  <th className="py-2 pr-4 text-right font-medium"># Buys</th>
                  <th className="py-2 font-medium">Last purchase</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.userId} className="border-b last:border-0">
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{u.language ?? '—'}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{formatCredits(u.purchasedCredits)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{u.topupCredits ? formatCredits(u.topupCredits) : '—'}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{formatCredits(u.balanceCredits)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{u.purchaseCount}</td>
                    <td className="py-2 text-muted-foreground">{u.lastPurchaseAt ? new Date(u.lastPurchaseAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && <p className="text-sm text-muted-foreground">No paid users yet.</p>
        )}
      </CardContent>
    </Card>
  )
}
